import type {
  BasePriceRuleFormValue,
  OPTION_TYPE,
} from 'src/interfaces/Template.interface'
import {
  getPriceRuleFieldDataId,
  getPriceRulePlatformFieldId,
  matchPriceRuleToField,
} from '../../utils/priceTemplateFieldMatch'
import {
  generateDefaultPriceRules,
  type PriceFieldForRules,
  type PriceRuleFormValue,
} from '../hooks/usePriceModifierFields'
import { resolvePriceIsDefaultFromApi } from '../../utils/priceIsDefaultApi'
import type { PriceIsDefaultFromApi } from '../../utils/priceIsDefaultApi'

type PriceHydrateField = PriceFieldForRules & {
  api_id?: string | number | null
  api_code?: string | null
}

type PriceHydrateRow = BasePriceRuleFormValue & {
  ruleId?: number | null
  destinationPlatformFieldDataId?: number | null
  isDefault?: boolean
  isDefaultPayloadKey?: PriceIsDefaultFromApi['payloadKey']
}

const deriveAdjustmentType = (val: number | undefined | null): 'INCREASE' | 'DECREASE' | 'NO_CHANGE' => {
  if (val == null || !Number.isFinite(Number(val))) return 'NO_CHANGE'
  if (Number(val) === 0) return 'INCREASE'
  return val > 0 ? 'INCREASE' : 'DECREASE'
}

export const unwrapPriceTemplatePayload = (payload: any): any => {
  if (!payload) return null
  if (Array.isArray(payload)) return payload
  if (payload.price_template && typeof payload.price_template === 'object') {
    return payload.price_template
  }
  return payload
}

export const resolvePriceTemplateId = (value: unknown): number | null => {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null
  }
  if (typeof value === 'string') {
    const num = Number(value)
    return Number.isFinite(num) && num > 0 ? num : null
  }
  if (typeof value === 'object') {
    const record = value as { id?: unknown; price_template?: unknown }
    return resolvePriceTemplateId(record.id) ?? resolvePriceTemplateId(record.price_template)
  }
  return null
}

const hasPerFieldRuleMetadata = (rule: any): boolean => {
  if (!rule || typeof rule !== 'object') return false
  const destId = getPriceRuleFieldDataId(rule) ?? getPriceRulePlatformFieldId(rule)
  const sourceId =
    rule.source_platform_field_id ??
    rule.source_platform_field?.id ??
    rule.source_platform_field ??
    rule.mapped_field
  const apiName =
    rule.destination_platform_field?.api_name ||
    rule.destination_platform_field_api_name ||
    rule.api_name
  return (
    destId != null ||
    sourceId != null ||
    Boolean(apiName) ||
    Boolean(rule.start_date) ||
    Boolean(rule.end_date)
  )
}

const getNestedRuleList = (payload: any): any[] | null => {
  const root = unwrapPriceTemplatePayload(payload)
  if (!root || Array.isArray(root)) return Array.isArray(root) ? root : null

  const nested =
    root.price_rules ||
    root.template_fields ||
    root.price_template_data ||
    root.priceTemplateData ||
    root.price_template_mapping_data ||
    root.mapping_data

  return Array.isArray(nested) && nested.length > 0 ? nested : null
}

/** True when template-mapping already has per-row dest/source/date data. */
export const hasPerFieldPriceRules = (payload: any): boolean => {
  const nested = getNestedRuleList(payload)
  if (!nested) return false
  return nested.some(hasPerFieldRuleMetadata)
}

export const isUsablePriceHydratePayload = (payload: any): boolean => {
  const root = unwrapPriceTemplatePayload(payload)
  if (Array.isArray(root) && root.length > 0) return true
  if (!root || typeof root !== 'object') return false
  return Boolean(root.repricing) || hasPerFieldPriceRules(payload)
}

/**
 * Legacy quick-list edit: only queue pendingPriceHydrateRef while the platform price
 * catalog is still loading or has rows to merge saved rules onto.
 */
export const shouldQueuePendingPriceHydrate = (
  priceFieldsLoading: boolean,
  catalogFieldCount: number
): boolean => priceFieldsLoading || catalogFieldCount > 0

/** Extra GET only when recipe has an id but no per-row rule list or root repricing. */
export const needsPriceTemplateDetailFetch = (
  recipePrice: any,
  priceId: number | null
): boolean => {
  if (priceId == null) return false
  if (hasPerFieldPriceRules(recipePrice)) return false

  const root = unwrapPriceTemplatePayload(recipePrice)
  if (root && !Array.isArray(root) && root.repricing) return false

  return true
}

export const mergePriceHydratePayloads = (recipePrice: any, detailPayload: any): any => {
  const nested = unwrapPriceTemplatePayload(recipePrice)
  const detail = unwrapPriceTemplatePayload(detailPayload)
  if (!detail) return nested
  if (!nested) return detail
  if (Array.isArray(detail)) return detail
  if (!detail.repricing && nested.repricing && !Array.isArray(nested)) {
    return { ...detail, repricing: nested.repricing }
  }
  return detail
}

/** Template-level default flag from GET (legacy single-repricing hydrate). */
export const extractPriceTemplateIsDefault = (payload: any): PriceIsDefaultFromApi | null => {
  const root = unwrapPriceTemplatePayload(payload)
  const rawRules = extractPriceTemplateRules(payload)

  for (const rule of rawRules) {
    const fromRule = resolvePriceIsDefaultFromApi(rule)
    if (fromRule) return fromRule
    const fromNested = resolvePriceIsDefaultFromApi(
      (rule as { price_template?: unknown })?.price_template
    )
    if (fromNested) return fromNested
  }

  if (root && !Array.isArray(root)) {
    const fromRoot = resolvePriceIsDefaultFromApi(root)
    if (fromRoot) return fromRoot
    return resolvePriceIsDefaultFromApi((root as { price_template?: unknown }).price_template)
  }

  return null
}

export const extractPriceTemplateRules = (payload: any): any[] => {
  const root = unwrapPriceTemplatePayload(payload)
  if (!root) return []
  if (Array.isArray(root)) return root

  const nested =
    root.price_rules ||
    root.template_fields ||
    root.price_template_data ||
    root.priceTemplateData ||
    root.price_template_mapping_data ||
    root.mapping_data

  if (Array.isArray(nested) && nested.length > 0) return nested
  if (root.repricing || root.id) return [root]
  return []
}

type MappingFieldRef = { id: number }

export type PriceMappingFieldLists = {
  nativeFields?: readonly MappingFieldRef[]
  customFields?: readonly MappingFieldRef[]
}

const fieldListHasId = (fields: readonly MappingFieldRef[] | undefined, id: number): boolean =>
  (fields ?? []).some((field) => Number(field.id) === id)

/**
 * Same order as the legacy price modal (`AddNewPriceTemplateModal`):
 * keep an explicit type; otherwise field type, then loaded native/custom lists, then source id.
 */
export const resolveListingProfilePriceMappingOption = (
  rule: any,
  lists?: PriceMappingFieldLists
): OPTION_TYPE => {
  const sourceId =
    rule?.source_platform_field_id ?? rule?.source_platform_field?.id ?? rule?.source_platform_field
  const sourceFieldType =
    rule?.source_platform_field?.field_type || rule?.source_platform_field?.fieldType
  const numericSourceId =
    sourceId != null && sourceId !== '' && Number.isFinite(Number(sourceId)) ? Number(sourceId) : null
  const serverMappingOption = rule?.mapping_option
  let mappingOption: OPTION_TYPE =
    serverMappingOption === 'DEFAULT' ||
    serverMappingOption === 'NATIVE' ||
    serverMappingOption === 'CUSTOM'
      ? serverMappingOption
      : 'DEFAULT'

  if (mappingOption === 'DEFAULT' && sourceFieldType) {
    mappingOption = sourceFieldType === 'NATIVE' ? 'NATIVE' : 'CUSTOM'
  }
  if (mappingOption === 'DEFAULT' && numericSourceId != null && fieldListHasId(lists?.nativeFields, numericSourceId)) {
    mappingOption = 'NATIVE'
  }
  if (mappingOption === 'DEFAULT' && numericSourceId != null && fieldListHasId(lists?.customFields, numericSourceId)) {
    mappingOption = 'CUSTOM'
  }
  if (mappingOption === 'DEFAULT' && numericSourceId != null) {
    const destDataId =
      rule?.destination_platform_field_data_id ??
      rule?.destination_platform_field_data?.id ??
      rule?.destination_platform_field_data
    mappingOption = destDataId ? 'NATIVE' : 'CUSTOM'
  }
  return mappingOption
}

const resolveMappedField = (rule: any): number | '' => {
  const mapped = rule?.mapped_field
  if (mapped && typeof mapped === 'object' && mapped.id != null) return Number(mapped.id)
  if (mapped != null && mapped !== '') return Number(mapped)
  const sourceId =
    rule?.source_platform_field_id ?? rule?.source_platform_field?.id ?? rule?.source_platform_field
  if (sourceId != null && sourceId !== '') return Number(sourceId)
  return ''
}

const applyRuleToRow = (
  row: PriceHydrateRow,
  rule: any,
  lists?: PriceMappingFieldLists
): PriceHydrateRow => {
  const repricingValue = rule?.repricing?.value
  const defaultRaw = rule?.value_str ?? rule?.value ?? rule?.default_value
  const mappedField = resolveMappedField(rule)
  const ruleIsDefault = resolvePriceIsDefaultFromApi(rule)
  return {
    ...row,
    ...(ruleIsDefault
      ? { isDefault: ruleIsDefault.value, isDefaultPayloadKey: ruleIsDefault.payloadKey }
      : {}),
    adjustmentMode: rule?.repricing?.type ?? row.adjustmentMode,
    adjustmentType: deriveAdjustmentType(repricingValue),
    adjustmentValue:
      repricingValue != null && Number.isFinite(Number(repricingValue))
        ? String(Math.abs(Number(repricingValue)))
        : row.adjustmentValue,
    destinationPlatformFieldId:
      getPriceRulePlatformFieldId(rule) != null
        ? Number(getPriceRulePlatformFieldId(rule))
        : row.destinationPlatformFieldId,
    mappingOption: resolveListingProfilePriceMappingOption(rule, lists),
    mappedField: mappedField !== '' && Number.isFinite(mappedField) ? mappedField : row.mappedField,
    defaultValue:
      defaultRaw != null && defaultRaw !== '' ? String(defaultRaw) : row.defaultValue,
    isPriceDate: !!(rule?.start_date || rule?.end_date) || row.isPriceDate,
    startDate: rule?.start_date ?? row.startDate,
    endDate: rule?.end_date ?? row.endDate,
    ruleId: rule?.id ?? row.ruleId,
    destinationPlatformFieldDataId:
      getPriceRuleFieldDataId(rule) != null
        ? Number(getPriceRuleFieldDataId(rule))
        : row.destinationPlatformFieldDataId,
  }
}

export const mapPriceTemplateToQuickListForm = (
  payload: any,
  fields: PriceHydrateField[],
  lists?: PriceMappingFieldLists
): {
  rules: PriceHydrateRow[]
  repricing: { type: string; value: number | null }
  adjustmentType: string
  templateIsDefault?: boolean
  templateIsDefaultPayloadKey?: PriceIsDefaultFromApi['payloadKey']
} => {
  const root = unwrapPriceTemplatePayload(payload)
  const rawRules = extractPriceTemplateRules(payload)
  const rootRepricing = Array.isArray(root) ? rawRules[0]?.repricing : root?.repricing
  const rawRepricingValue = rootRepricing?.value
  const repricing = {
    type: rootRepricing?.type ?? 'FLAT',
    value:
      rawRepricingValue != null && Number.isFinite(Number(rawRepricingValue))
        ? Number(rawRepricingValue)
        : null,
  }
  const adjustmentType =
    repricing.value != null && repricing.value < 0 ? 'decrease' : 'increase'

  const templateIsDefaultMeta = extractPriceTemplateIsDefault(payload)
  const templateIsDefault = templateIsDefaultMeta?.value
  const templateIsDefaultPayloadKey = templateIsDefaultMeta?.payloadKey

  if (!fields.length) {
    return {
      rules: [],
      repricing,
      adjustmentType,
      templateIsDefault,
      templateIsDefaultPayloadKey,
    }
  }

  const defaults = generateDefaultPriceRules(fields)
  const matched = defaults.map((row) => {
    const rule = rawRules.find((item) => {
      const field = matchPriceRuleToField(item, fields)
      return field?.id === row.templateFieldId
    })
    return rule ? applyRuleToRow(row, rule, lists) : row
  })

  const anyMatched = matched.some((row, index) => row !== defaults[index])
  const fallbackRule = {
    ...(rawRules[0] ?? {}),
    repricing: rawRules[0]?.repricing ?? (Array.isArray(root) ? undefined : root?.repricing),
  }
  // Legacy only: a single summary rule with no dest identity fills the first row.
  if (!anyMatched && rawRules.length <= 1 && fallbackRule.repricing) {
    matched[0] = applyRuleToRow(defaults[0], fallbackRule, lists)
  }

  return {
    rules: matched,
    repricing,
    adjustmentType,
    templateIsDefault,
    templateIsDefaultPayloadKey,
  }
}

/** Update mapping type for one price row; clears value fields when type changes. */
export const applyPriceRuleMappingOptionChange = (
  rules: PriceRuleFormValue[],
  rowIndex: number,
  option: OPTION_TYPE
): PriceRuleFormValue[] => {
  if (rowIndex < 0 || rowIndex >= rules.length) return rules
  const updated = [...rules]
  updated[rowIndex] = {
    ...updated[rowIndex],
    mappingOption: option,
    defaultValue: '',
    mappedField: '',
  }
  return updated
}
